// frontend/TwitterAnalytics/modules/ContentAggregation.jsx
/**
 * Module 1: Content Aggregation
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton,
  Card, CardMedia, CardContent, CircularProgress, Tooltip, 
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Sync as SyncIcon,
  Favorite as LikeIcon,
  Repeat as RetweetIcon,
  ChatBubble as ReplyIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Gif as GifIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from "../../../../utils/axiosInstance";

const ContentAggregation = ({ apiTier, onShowSnackbar }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, grid, media
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    post_type: '',
    has_media: '',
    media_type: '',
    date_from: '',
    date_to: '',
    sort_by: 'posted_at',
    sort_order: 'desc'
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0
  });

  // Media gallery state
  const [mediaItems, setMediaItems] = useState([]);

  useEffect(() => {
    fetchPosts();
    fetchStats();
  }, [filters, pagination.page, pagination.per_page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/twitter/posts', {
        params: {
          ...filters,
          page: pagination.page,
          per_page: pagination.per_page
        }
      });
      const response = res.data;
      if (response.success) {
        setPosts(response.data);
        setPagination(prev => ({ ...prev, total: response.pagination.total }));
      }
    } catch (err) {
      onShowSnackbar('Failed to fetch posts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/posts/stats');
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchMediaGallery = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/posts/media', {
        params: { page: 1, per_page: 50 }
      });
      if (res.data.success) {
        setMediaItems(res.data.data);
      }
    } catch (err) {
      onShowSnackbar('Failed to fetch media', 'error');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axiosInstance.post('/api/twitter/posts/sync');
      const response = res.data;
      if (response.success) {
        onShowSnackbar(`Synced ${response.new_posts} new, updated ${response.updated_posts} posts`, 'success');
        fetchPosts();
        fetchStats();
      } else {
        onShowSnackbar(response.message || 'Sync failed', 'error');
      }
    } catch (err) {
      onShowSnackbar(err.message || 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post from analytics?')) return;
    try {
      await axiosInstance.delete(`/api/twitter/posts/${postId}`);
      onShowSnackbar('Post deleted', 'success');
      fetchPosts();
      fetchStats();
    } catch (err) {
      onShowSnackbar('Failed to delete post', 'error');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getPostTypeColor = (type) => {
    const colors = {
      tweet: 'primary',
      retweet: 'success',
      reply: 'info',
      quote: 'warning'
    };
    return colors[type] || 'default';
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return <VideoIcon fontSize="small" />;
      case 'animated_gif': return <GifIcon fontSize="small" />;
      default: return <ImageIcon fontSize="small" />;
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <Box>
      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.total_posts}</Typography>
              <Typography variant="body2" color="textSecondary">Total Posts</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{formatNumber(stats.total_engagement?.impressions)}</Typography>
              <Typography variant="body2" color="textSecondary">Impressions</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{formatNumber(stats.total_engagement?.likes)}</Typography>
              <Typography variant="body2" color="textSecondary">Likes</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{formatNumber(stats.total_engagement?.retweets)}</Typography>
              <Typography variant="body2" color="textSecondary">Retweets</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{formatNumber(stats.total_engagement?.replies)}</Typography>
              <Typography variant="body2" color="textSecondary">Replies</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.average_engagement_score}</Typography>
              <Typography variant="body2" color="textSecondary">Avg Engagement</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Action Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Posts'}
            </Button>
            
            <Chip 
              label={apiTier === 'free' ? 'Free: 10 posts/sync' : '100 posts/sync'}
              size="small"
              color={apiTier === 'free' ? 'warning' : 'success'}
            />
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="list"><ListViewIcon /></ToggleButton>
            <ToggleButton value="grid"><GridViewIcon /></ToggleButton>
            <ToggleButton value="media"><ImageIcon /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Post Type</InputLabel>
              <Select
                value={filters.post_type}
                label="Post Type"
                onChange={(e) => handleFilterChange('post_type', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="tweet">Tweet</MenuItem>
                <MenuItem value="retweet">Retweet</MenuItem>
                <MenuItem value="reply">Reply</MenuItem>
                <MenuItem value="quote">Quote</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Media</InputLabel>
              <Select
                value={filters.has_media}
                label="Media"
                onChange={(e) => handleFilterChange('has_media', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">With Media</MenuItem>
                <MenuItem value="false">No Media</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sort_by}
                label="Sort By"
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
              >
                <MenuItem value="posted_at">Date</MenuItem>
                <MenuItem value="like_count">Likes</MenuItem>
                <MenuItem value="reply_count">Replies</MenuItem>
                <MenuItem value="engagement_score">Engagement</MenuItem>
                <MenuItem value="lowest_engagement">Lowest Engagement</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Posts List View */}
      {viewMode === 'list' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Post</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Media</TableCell>
                <TableCell align="center"><LikeIcon fontSize="small" /></TableCell>
                <TableCell align="center"><RetweetIcon fontSize="small" /></TableCell>
                <TableCell align="center"><ReplyIcon fontSize="small" /></TableCell>
                <TableCell align="center"><ViewIcon fontSize="small" /></TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No posts found. Click "Sync Posts" to fetch your tweets.
                  </TableCell>
                </TableRow>
              ) : (
                posts.map((post) => (
                  <TableRow key={post.id} hover>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap>
                        {post.text?.substring(0, 100)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={post.post_type} 
                        size="small" 
                        color={getPostTypeColor(post.post_type)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {post.has_media && (
                        <Tooltip title={post.media_type}>
                          {getMediaIcon(post.media_type)}
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">{formatNumber(post.like_count)}</TableCell>
                    <TableCell align="center">{formatNumber(post.retweet_count)}</TableCell>
                    <TableCell align="center">{formatNumber(post.reply_count)}</TableCell>
                    <TableCell align="center">{formatNumber(post.impression_count)}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={post.engagement_score?.toFixed(0)}
                        size="small"
                        color={post.engagement_score > 100 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {post.posted_at && format(new Date(post.posted_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Open on Twitter">
                          <IconButton 
                            size="small" 
                            href={post.post_url} 
                            target="_blank"
                          >
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(post.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={(e, page) => setPagination(prev => ({ ...prev, page: page + 1 }))}
            rowsPerPage={pagination.per_page}
            onRowsPerPageChange={(e) => setPagination(prev => ({ 
              ...prev, 
              per_page: parseInt(e.target.value), 
              page: 1 
            }))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {posts.map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.id}>
              <Card>
                {post.media_url && (
                  <CardMedia
                    component="img"
                    height="140"
                    image={post.media_url}
                    alt="Media"
                  />
                )}
                <CardContent>
                  <Typography variant="body2" gutterBottom noWrap>
                    {post.text?.substring(0, 80)}...
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Box display="flex" gap={1}>
                      <Chip icon={<LikeIcon />} label={formatNumber(post.like_count)} size="small" />
                      <Chip icon={<ReplyIcon />} label={formatNumber(post.reply_count)} size="small" />
                    </Box>
                    <Chip 
                      label={post.post_type} 
                      size="small" 
                      color={getPostTypeColor(post.post_type)}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Media Gallery View */}
      {viewMode === 'media' && (
        <Box>
          <Button onClick={fetchMediaGallery} sx={{ mb: 2 }}>Load Media Gallery</Button>
          <Grid container spacing={1}>
            {mediaItems.map((item, idx) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={idx}>
                <Card sx={{ cursor: 'pointer' }}>
                  <CardMedia
                    component="img"
                    height="120"
                    image={item.media_url}
                    alt="Media"
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
                    {getMediaIcon(item.media_type)}
                    <Typography variant="caption">{formatNumber(item.like_count)} ❤️</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ContentAggregation;